import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Grid, Card, CardContent, Chip,
  Accordion, AccordionSummary, AccordionDetails, Avatar,
} from '@mui/material';
import {
  SwapHoriz, CheckCircle, Schedule, FolderCopy,
  Label, Security, CloudUpload, Speed, ArrowForward,
  ExpandMore, Email, Contacts, CalendarMonth, LockPerson,
  SupportAgent, BarChart, Verified, DoneAll, DataObject,
  AdminPanelSettings, StarBorder,
} from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

const providers = [
  { name: 'Gmail / Google Workspace', color: '#EA4335', letter: 'G' },
  { name: 'Microsoft 365 / Outlook', color: '#0078D4', letter: 'M' },
  { name: 'Zoho Mail', color: '#E42527', letter: 'Z' },
  { name: 'Yahoo Mail', color: '#6001D2', letter: 'Y' },
  { name: 'cPanel / Plesk Mail', color: '#FF6C2C', letter: 'C' },
  { name: 'Any IMAP / POP3', color: '#16a34a', letter: 'I' },
];

const steps = [
  {
    step: '01', icon: <DataObject sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Connect Your Source',
    desc: 'Enter your existing provider credentials. We support IMAP, POP3, and direct API connections for all major email platforms. Your credentials are never stored.',
    badge: 'Secure Connection',
  },
  {
    step: '02', icon: <DoneAll sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Choose What to Migrate',
    desc: 'Select emails, folders, labels, contacts, calendar events, and attachments. You can filter by date range or folder — skip what you don\'t need.',
    badge: 'Full Control',
  },
  {
    step: '03', icon: <Speed sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Live Migration Dashboard',
    desc: 'Watch your data move in real time. Our parallel processing engine handles thousands of emails per minute. Estimated completion times shown upfront.',
    badge: 'Real-time Tracking',
  },
  {
    step: '04', icon: <Verified sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Verify & Go Live',
    desc: 'Run our automated integrity checker to confirm 100% data accuracy. Then update your MX records and go live — with zero downtime during the switch.',
    badge: 'Zero Downtime',
  },
];

const migrated = [
  { icon: <Email sx={{ fontSize: 26, color: '#dc2626' }} />, title: 'Emails & Threads', desc: 'Every email including read/unread state, stars, flags and full thread structure.' },
  { icon: <FolderCopy sx={{ fontSize: 26, color: '#dc2626' }} />, title: 'Folder Hierarchy', desc: 'Nested folders, IMAP mailboxes and labels recreated exactly as they were.' },
  { icon: <Label sx={{ fontSize: 26, color: '#dc2626' }} />, title: 'Labels & Tags', desc: 'Gmail labels and provider-specific tags are mapped intelligently to CruvzMail labels.' },
  { icon: <Contacts sx={{ fontSize: 26, color: '#dc2626' }} />, title: 'Contacts & Groups', desc: 'Full contact book including distribution lists, VCards and contact metadata.' },
  { icon: <CalendarMonth sx={{ fontSize: 26, color: '#dc2626' }} />, title: 'Calendar Events', desc: 'All events, recurring meetings, invites, and reminders migrated via iCal standard.' },
  { icon: <CloudUpload sx={{ fontSize: 26, color: '#dc2626' }} />, title: 'Attachments', desc: 'Full-resolution attachments in every email. No size caps on individual file migration.' },
  { icon: <Security sx={{ fontSize: 26, color: '#dc2626' }} />, title: 'TLS Encryption', desc: 'All data in-transit is protected with TLS 1.3. We never read or store your emails.' },
  { icon: <Schedule sx={{ fontSize: 26, color: '#dc2626' }} />, title: 'Scheduled Sync', desc: 'Run migration during off-hours or schedule delta-syncs after the initial move.' },
  { icon: <AdminPanelSettings sx={{ fontSize: 26, color: '#dc2626' }} />, title: 'Bulk / CSV Import', desc: 'Migrate entire organizations at once using a simple CSV file with user credentials.' },
];

const stats = [
  { value: '50,000+', label: 'Mailboxes Migrated' },
  { value: '99.99%', label: 'Data Integrity Rate' },
  { value: '< 2 min', label: 'Avg Setup Time' },
  { value: '0', label: 'Downtime During Switch' },
];

const faqs = [
  { q: 'Will my users lose access to email during migration?', a: 'No. Migration runs in parallel with your current mail server. Users keep full access throughout. We only cut over MX records once you confirm everything looks perfect.' },
  { q: 'How long does migration take?', a: 'A single mailbox with 10,000 emails typically takes 15–30 minutes. Large organizations with hundreds of accounts can be processed overnight using our bulk scheduler.' },
  { q: 'Is my password stored on CruvzMail servers?', a: 'Never. Source credentials are held in memory only for the duration of the migration and discarded immediately after. We are GDPR-compliant by design.' },
  { q: 'Can I migrate only certain folders or date ranges?', a: 'Yes. During setup you can include or exclude any folder and set a date range (e.g. only emails from the last 2 years). Everything else is skipped.' },
  { q: 'What if some emails fail to migrate?', a: 'Our engine produces a detailed report of every item attempted. Failed items are retried automatically up to 3 times. You can then re-run just the failed items with one click.' },
  { q: 'Do you support enterprise / on-premise Exchange?', a: 'Yes. We support Exchange via EWS (Exchange Web Services), and on-premise IMAP/POP3 servers with custom ports and SSL configurations.' },
];

const MigrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>(false);

  return (
    <PublicLayout>

      {/* ── HERO ── */}
      <Box sx={{
        pt: { xs: '120px', md: '140px' }, pb: { xs: 10, md: 14 }, px: { xs: 2, md: 5 },
        position: 'relative', overflow: 'hidden', textAlign: 'center',
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 45%, #1e0a0a 100%)',
      }}>
        {/* Decorative orbs */}
        <Box sx={{ position: 'absolute', top: -120, left: -120, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,38,38,0.18) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -80, right: -80, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <Box sx={{ maxWidth: 860, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Chip
            icon={<SwapHoriz sx={{ color: '#fca5a5 !important' }} />}
            label="Zero-Downtime Migration"
            sx={{ mb: 3, bgcolor: 'rgba(220,38,38,0.15)', color: '#fca5a5', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(220,38,38,0.3)', letterSpacing: '0.04em' }}
          />
          <Typography variant="h1" sx={{
            fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.8rem' }, lineHeight: 1.1, mb: 3,
            background: 'linear-gradient(135deg, #ffffff 30%, #fca5a5 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            Switch to CruvzMail<br />in Under 30 Minutes
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.2rem' }, color: 'rgba(255,255,255,0.65)', maxWidth: 620, mx: 'auto', lineHeight: 1.8, mb: 5 }}>
            Migrate from any provider with zero data loss and zero downtime. Emails, folders, contacts, calendars — fully automated and TLS-encrypted from start to finish.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              onClick={() => navigate('/signup')}
              endIcon={<ArrowForward />}
              sx={{
                px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none',
                color: '#fff', bgcolor: '#dc2626', boxShadow: '0 0 40px rgba(220,38,38,0.4)',
                '&:hover': { bgcolor: '#b91c1c', boxShadow: '0 0 50px rgba(220,38,38,0.6)' },
                transition: 'all 0.2s',
              }}
            >
              Start Free Migration
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              sx={{
                px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none',
                color: '#fff', border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.05)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Talk to an Expert
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── STATS BAR ── */}
      <Box sx={{ bgcolor: '#dc2626', py: { xs: 4, md: 3 }, px: { xs: 2, md: 5 } }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Grid container spacing={2} justifyContent="center">
            {stats.map((s) => (
              <Grid size={{ xs: 6, md: 3 }} key={s.label}>
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#fff', lineHeight: 1.1 }}>{s.value}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500, mt: 0.3 }}>{s.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── PROVIDERS ── */}
      <Box sx={{ py: { xs: 7, md: 10 }, px: { xs: 2, md: 5 }, bgcolor: '#fff', textAlign: 'center' }}>
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 2 }}>
            Migrate from any provider
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 1.5, fontSize: { xs: '1.6rem', md: '2rem' } }}>
            Works With Every Email Platform
          </Typography>
          <Typography sx={{ color: '#64748b', mb: 5, fontSize: '1rem' }}>
            No matter where your emails live today, we migrate them seamlessly.
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {providers.map((p) => (
              <Grid size={{ xs: 6, sm: 4, md: 2 }} key={p.name}>
                <Box sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
                  p: 3, borderRadius: 3, border: '1px solid #e2e8f0',
                  transition: 'all 0.2s', cursor: 'default',
                  '&:hover': { borderColor: p.color, boxShadow: `0 4px 20px ${p.color}20`, transform: 'translateY(-3px)' },
                }}>
                  <Avatar sx={{ bgcolor: p.color, width: 48, height: 48, fontWeight: 800, fontSize: '1.2rem' }}>{p.letter}</Avatar>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#334155', textAlign: 'center', lineHeight: 1.3 }}>{p.name}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── HOW IT WORKS ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>How It Works</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1.5 }}>
              From Old Provider to CruvzMail — In 4 Steps
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '1.05rem', maxWidth: 560, mx: 'auto' }}>
              Our guided wizard takes care of everything. No technical expertise required.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {steps.map((s, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={s.step}>
                <Box sx={{
                  position: 'relative', height: '100%',
                  p: 3.5, borderRadius: 3, bgcolor: '#fff',
                  border: '1px solid #e2e8f0',
                  transition: 'all 0.3s',
                  '&:hover': { boxShadow: '0 12px 40px rgba(0,0,0,0.08)', transform: 'translateY(-4px)', borderColor: '#dc2626' },
                }}>
                  {/* Step number accent */}
                  <Typography sx={{ position: 'absolute', top: 16, right: 20, fontSize: '3rem', fontWeight: 900, color: '#f1f5f9', lineHeight: 1, zIndex: 0 }}>{s.step}</Typography>
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{
                      width: 52, height: 52, borderRadius: 2.5, mb: 2.5,
                      background: 'linear-gradient(135deg, #dc2626, #991b1b)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 4px 16px rgba(220,38,38,0.3)',
                    }}>
                      {s.icon}
                    </Box>
                    <Chip label={s.badge} size="small" sx={{ mb: 1.5, bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 600, fontSize: '0.68rem', border: '1px solid #fecaca' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a', mb: 1 }}>{s.title}</Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.75 }}>{s.desc}</Typography>
                  </Box>
                  {/* Connector arrow (not on last) */}
                  {i < steps.length - 1 && (
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, position: 'absolute', right: -18, top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}>
                      <ArrowForward sx={{ color: '#cbd5e1', fontSize: '1.2rem' }} />
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── WHAT GETS MIGRATED ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Complete Data Transfer</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1.5 }}>
              Everything Moves. Nothing Gets Left Behind.
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '1.05rem', maxWidth: 560, mx: 'auto' }}>
              Our engine covers every data type your team relies on, with 100% fidelity guaranteed.
            </Typography>
          </Box>
          <Grid container spacing={2.5}>
            {migrated.map((f) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.title}>
                <Box sx={{
                  display: 'flex', gap: 2, p: 2.5, borderRadius: 2.5,
                  border: '1px solid #f1f5f9', height: '100%',
                  transition: 'all 0.25s',
                  '&:hover': { borderColor: '#fecaca', bgcolor: '#fffbfb', boxShadow: '0 4px 20px rgba(220,38,38,0.06)' },
                }}>
                  <Box sx={{ flexShrink: 0, width: 44, height: 44, borderRadius: 2, bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {f.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', mb: 0.4 }}>{f.title}</Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.7 }}>{f.desc}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── WHY CRUVZMAIL ── */}
      <Box sx={{
        py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 },
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #1a0000 100%)',
      }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#fca5a5', mb: 1.5 }}>Why CruvzMail</Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: '1.8rem', md: '2.2rem' }, mb: 2, lineHeight: 1.25 }}>
                Not Just Another Email. A Complete Business Platform.
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.8, mb: 4, fontSize: '0.95rem' }}>
                CruvzMail is a self-hosted, privacy-first email platform built for Indian businesses — with DriveX cloud storage, Docs & Sheets collaboration, built-in helpdesk, and full admin control. Your data stays on your servers, forever.
              </Typography>
              <Button
                onClick={() => navigate('/signup')}
                endIcon={<ArrowForward />}
                sx={{ px: 3.5, py: 1.4, borderRadius: '10px', fontWeight: 700, textTransform: 'none', fontSize: '0.9rem', bgcolor: '#dc2626', color: '#fff', '&:hover': { bgcolor: '#b91c1c' } }}
              >
                Create Free Account
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Grid container spacing={2}>
                {[
                  { icon: <LockPerson sx={{ color: '#fca5a5', fontSize: 22 }} />, title: 'Your Data, Your Servers', desc: 'Fully self-hosted on your infrastructure. No third-party can access your email.' },
                  { icon: <StarBorder sx={{ color: '#fbbf24', fontSize: 22 }} />, title: 'Custom Domain Branding', desc: 'White-label the entire platform with your logo, colors, and domain.' },
                  { icon: <BarChart sx={{ color: '#34d399', fontSize: 22 }} />, title: 'Advanced Admin Console', desc: 'Domain management, user quotas, DKIM, spam filter, delivery reports — all in one dashboard.' },
                  { icon: <SupportAgent sx={{ color: '#60a5fa', fontSize: 22 }} />, title: 'Built-in Helpdesk', desc: 'Embedded ticket system, knowledge base, and customer support — no third-party tools needed.' },
                ].map((item) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={item.title}>
                    <Box sx={{
                      p: 2.5, borderRadius: 2.5,
                      border: '1px solid rgba(255,255,255,0.08)',
                      bgcolor: 'rgba(255,255,255,0.04)',
                      backdropFilter: 'blur(8px)',
                      transition: 'all 0.2s',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', borderColor: 'rgba(220,38,38,0.4)' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.icon}</Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#fff' }}>{item.title}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 }}>{item.desc}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* ── FAQ ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 780, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>FAQ</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
              Migration Questions Answered
            </Typography>
          </Box>
          {faqs.map((faq, i) => (
            <Accordion
              key={i}
              expanded={expanded === `p${i}`}
              onChange={(_, isExp) => setExpanded(isExp ? `p${i}` : false)}
              elevation={0}
              sx={{
                mb: 1.5, border: '1px solid #e2e8f0', borderRadius: '12px !important',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { borderColor: '#fecaca', boxShadow: '0 4px 20px rgba(220,38,38,0.06)' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#dc2626' }} />} sx={{ px: 3, py: 0.5 }}>
                <Typography sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pt: 0, pb: 2.5 }}>
                <Typography sx={{ color: '#64748b', lineHeight: 1.8, fontSize: '0.875rem' }}>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>

      {/* ── FINAL CTA ── */}
      <Box sx={{
        py: { xs: 10, md: 14 }, px: { xs: 2, md: 5 }, textAlign: 'center',
        background: 'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 640, mx: 'auto' }}>
          <Chip label="Free for all plans" sx={{ mb: 3, bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(255,255,255,0.25)' }} />
          <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, color: '#fff', mb: 2, lineHeight: 1.15 }}>
            Ready to Make the Switch?
          </Typography>
          <Typography sx={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.75)', mb: 5, lineHeight: 1.75 }}>
            Our migration team will guide you through every step. Most organizations are fully live within the same business day.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              onClick={() => navigate('/signup')}
              endIcon={<ArrowForward />}
              sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#dc2626', bgcolor: '#fff', '&:hover': { bgcolor: '#fef2f2' } }}
            >
              Start Free Migration
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.35)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              Schedule a Demo
            </Button>
          </Box>
          <Box sx={{ mt: 4, display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            {['No credit card required', 'Migration assistance included', '24/7 support'].map(t => (
              <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                <CheckCircle sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
                <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.65)' }}>{t}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

    </PublicLayout>
  );
};

export default MigrationPage;
